import { UseQueryResult } from '@tanstack/react-query';
import { createContext, useContext } from 'react';

import { Case } from '../api/hscan/case';
import { Page } from '../api/page';
import {
  DownloadProgressEventHandler,
  DownloadOptions,
  CaseParamsWithPage,
  useDownloadImage,
  useSubscribeDownloadProgress,
  useDicomImage,
  useCase,
  useGetLinkUrl,
} from '../hooks/caseHook';
import { QueryOption } from '../hooks/react-query-type';

export type UseCaseArray = (
  options?: QueryOption<Case[]>,
) => UseQueryResult<Case[], unknown>;
export type UseCasePage = (
  params: CaseParamsWithPage,
  options?: QueryOption<Page<Case>>,
) => UseQueryResult<Page<Case>, unknown>;

export interface CaseListContextType {
  useCase: UseCaseArray | UseCasePage;
  useDicomImage(imageId: string): UseQueryResult<File>;
  useGetLinkUrl(): (caseIds: string[]) => Promise<string>;
  useSubscribeDownloadProgress(
    sseKey: string,
    downloadProgressEventHandler: Omit<
      DownloadProgressEventHandler,
      'onStart' | 'onComplete'
    >,
  ): { subscribeDownloadProgress(): void };
  useDownloadImage(
    caseIds: string[],
    sseKey: string,
    options?: DownloadOptions,
  ): () => void;
}

const defaultCaseListContext: CaseListContextType = {
  useCase,
  useDicomImage,
  useGetLinkUrl,
  useSubscribeDownloadProgress,
  useDownloadImage,
};

export const CaseListContext = createContext<CaseListContextType>(
  defaultCaseListContext,
);

export const useCaseListContext = () =>
  useContext<CaseListContextType>(CaseListContext);
